import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../app/hooks";
import LeftMenu from "../components/LeftMenu";
import AttendancePanel from "../components/attendance/AttendancePanel";
import CancelOccurrenceAction from "../components/attendance/CancelOccurrenceAction";
import OccurrenceTopicSelect from "../components/training/OccurrenceTopicSelect";
import { Session } from "../features/sessions/models/Session";
import { fetchSessionById } from "../features/sessions/slice-extensions/sessions-slice-extension";
import { formatTimeDisplay } from "../features/sessions/utils/DisplayMappers";
import { ROUTES } from "../routes";

/**
 * Phone-first attendance flow, step 2: mark one occurrence. Every member defaults
 * to Attended (seeded by AttendancePanel) — the fast path is a single Save tap.
 */
const AttendanceOccurrencePage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { sessionId, date } = useParams<{ sessionId: string; date: string }>();

  // Local copy so a late response can't clobber another screen's currentSession.
  const [session, setSession] = useState<Session | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    let active = true;
    dispatch(fetchSessionById(sessionId)).then((result) => {
      if (!active) return;
      if (fetchSessionById.fulfilled.match(result)) setSession(result.payload as Session);
      else setFailed(true);
    });
    return () => {
      active = false;
    };
  }, [dispatch, sessionId]);

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full p-4 pb-10 lg:px-10">
          <div className="lg:max-w-[720px]">
            <div className="space-y-4 py-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => navigate(`${ROUTES.ATTENDANCE}?date=${date ?? ""}`)}
                  className="btn btn-secondary"
                >
                  <ChevronLeft size={18} />
                  <span>{t("sessions.attendance.backToDay")}</span>
                </button>
                {session && (
                  <div className="flex items-center gap-3">
                    <Link
                      to={ROUTES.SESSION_DETAILS.replace(":id", session.id)}
                      className="inline-flex items-center gap-1 text-sm text-primary-40 hover:underline dark:text-primary-90"
                    >
                      <span>{t("sessions.attendance.sessionDetails")}</span>
                      <ChevronRight size={16} />
                    </Link>
                    {/* Cancelling the date sits with the other header actions, not below the fold */}
                    {date && <CancelOccurrenceAction sessionId={session.id} date={date} />}
                  </div>
                )}
              </div>

              {failed && (
                <p className="text-sm text-red-600 dark:text-red-400">
                  {t("sessions.editScope.occurrenceNotFound")}
                </p>
              )}

              {session && (
                <>
                  <div>
                    <h1>{session.title}</h1>
                    <p className="mt-1 text-grey-30 dark:text-grey-70">
                      {formatTimeDisplay(session.timeLocal)}
                      {session.duration ? ` · ${session.duration} min` : ""}
                    </p>
                  </div>
                  {date && <OccurrenceTopicSelect sessionId={session.id} date={date} />}
                  <div className="with-border rounded-2xl p-4 lg:p-6">
                    <AttendancePanel session={session} occurrenceDate={date} embedded />
                  </div>
                </>
              )}
              {!session && !failed && (
                <p className="text-sm text-grey-40 dark:text-grey-60">{t("sessions.sessionLoading")}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AttendanceOccurrencePage;
